import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import ReminderTemplateSelect from '../components/ReminderTemplateSelect';
import { MAX_REMINDER_STEPS, newStepRow } from '../utils/collectionSettingsForm';

const CHANNEL_COLUMNS = [
  { channel: 'whatsapp', label: 'Plantilla de WhatsApp', offLabel: 'Plantilla de WhatsApp (canal desactivado)' },
  { channel: 'email', label: 'Plantilla de correo', offLabel: 'Plantilla de correo (canal desactivado)' },
];
const NO_CHANNELS = [];
const NO_TEMPLATE_ERRORS = {};

// Mensaje del error de PLANTILLA de una fila, por canal (claves i18n). Son las plantillas que
// billing rechazó al guardar (`invalid_template_reference`), no los días del paso.
const TEMPLATE_ERROR_LABELS = {
  email: 'La plantilla de correo de este paso ya no está disponible: elegí otra.',
  whatsapp: 'La plantilla de WhatsApp de este paso ya no está disponible: elegí otra.',
};

/**
 * Pasos de la regla automática como filas: días de atraso y, por cada canal activo, la
 * plantilla del paso (`null` = la plantilla por defecto del canal). Hasta 6 pasos.
 *
 * Son filas y no un texto libre ("1, 7, 15") porque cada paso lleva sus plantillas: al
 * cambiar un número en un texto no se sabría a qué paso pertenecía la asignación.
 *
 * Un canal apagado conserva sus asignaciones (vuelven al encenderlo) y billing las cuenta
 * como uso: una plantilla asignada ahí no se puede archivar. Por eso su columna se sigue
 * mostrando, rotulada como desactivada, mientras alguna fila tenga plantilla en ese canal
 * (`pinnedChannels`): es la única forma de verla y de quitarla.
 *
 * @param {{ baseId: string, rows: object[], onChange: (rows: object[]) => void,
 *   channels: { email: boolean, whatsapp: boolean }, templatesSupported: boolean,
 *   catalog: object|null, defaultLabels: { email: string, whatsapp: string },
 *   pinnedChannels?: string[], rowErrors?: Record<string, string>,
 *   templateErrors?: Record<string, { email?: boolean, whatsapp?: boolean }>,
 *   error?: string|null, hint?: React.ReactNode }} props
 *   `defaultLabels`: rótulo ya traducido de la opción "por defecto" de cada canal.
 *   `rowErrors`: por fila, la clave i18n de sus DÍAS de atraso (marca el campo de días).
 *   `templateErrors`: por fila y canal, la plantilla que billing rechazó al guardar (marca ese
 *   selector; son errores distintos y no se mezclan con los de días).
 */
export default function ReminderStepsEditor({
  baseId,
  rows,
  onChange,
  channels,
  templatesSupported,
  catalog,
  defaultLabels,
  pinnedChannels = NO_CHANNELS,
  rowErrors = {},
  templateErrors = NO_TEMPLATE_ERRORS,
  error,
  hint,
}) {
  const { t } = useTranslation();
  const columns = templatesSupported
    ? CHANNEL_COLUMNS.filter((c) => channels[c.channel] || pinnedChannels.includes(c.channel))
    : [];
  const hasOffColumns = columns.some((c) => !channels[c.channel]);
  const hintId = `${baseId}-steps-hint`;

  const updateRow = (key, patch) =>
    onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  const removeRow = (key) => onChange(rows.filter((row) => row.key !== key));

  return (
    <fieldset className="space-y-2" aria-describedby={hintId}>
      <legend className="text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
        {t('Pasos (días de atraso)')}
      </legend>

      {rows.length > 0 && (
        <ul className="m-0 list-none space-y-2 p-0">
          {rows.map((row, index) => {
            const rowError = rowErrors[row.key];
            const errorId = `${baseId}-step-${row.key}-error`;
            return (
              <li key={row.key} className="space-y-1.5 rounded-md border border-border p-3">
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex items-center gap-2 text-sm text-fg">
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={365}
                      className="w-24"
                      aria-label={t('Días de atraso del paso {{n}}', { n: index + 1 })}
                      value={row.days}
                      invalid={!!rowError}
                      aria-describedby={rowError ? errorId : undefined}
                      onChange={(e) => updateRow(row.key, { days: e.target.value })}
                    />
                    <span>{t('días de atraso')}</span>
                  </div>
                  {columns.map(({ channel, label, offLabel }) => (
                    <div key={channel} className="min-w-[12rem] flex-1">
                      <ReminderTemplateSelect
                        label={t(channels[channel] ? label : offLabel)}
                        channel={channel}
                        value={row[`${channel}TemplateId`]}
                        onChange={(templateId) => updateRow(row.key, { [`${channel}TemplateId`]: templateId })}
                        catalog={catalog}
                        nullLabel={defaultLabels[channel]}
                        error={
                          templateErrors[row.key]?.[channel]
                            ? t(TEMPLATE_ERROR_LABELS[channel])
                            : undefined
                        }
                      />
                    </div>
                  ))}
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('Quitar el paso {{n}}', { n: index + 1 })}
                    onClick={() => removeRow(row.key)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {rowError && (
                  <p id={errorId} className="text-xs text-status-error">
                    {t(rowError)}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {hasOffColumns && (
        <p className="text-xs text-fg-muted">
          {t('Las plantillas de un canal desactivado no se usan, pero siguen asignadas y no dejan archivar esa plantilla. Para quitarlas, elegí la opción por defecto.')}
        </p>
      )}

      <Button
        variant="outline"
        size="sm"
        iconLeft={<Plus className="h-4 w-4" />}
        disabled={rows.length >= MAX_REMINDER_STEPS}
        onClick={() => onChange([...rows, newStepRow()])}
      >
        {t('Agregar paso')}
      </Button>

      {error ? (
        <p id={hintId} className="text-xs text-status-error">
          {t(error)}
        </p>
      ) : (
        <p id={hintId} className="text-xs text-fg-muted">
          {hint}
        </p>
      )}
    </fieldset>
  );
}
